import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import LocationProps from "../../../types/Location";

type Props = {
    translations: {
        i18nButtonText: string;
        i18nInputPlaceholder: string;
        i18nNoResults: string;
    };
};

const WeeklyOffersSelection: React.FC<Props> = ({ translations }) => {
    const allLocations: LocationProps[] = JSON.parse(
        document.getElementById("locations")?.textContent || "[]",
    );

    const [search, setSearch] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const filtered = useMemo(() => {
        const terms = search.trim().toLowerCase().split(/\s+/);

        return allLocations.filter((loc) => {
            const fullText = `
            ${loc.Name}
            ${loc.Street}
            ${loc.Zip}
            ${loc.City}
        `.toLowerCase();

            return terms.every((term) => fullText.includes(term));
        });
    }, [allLocations, search]);

    const closeDropdown = useCallback((restoreFocus = false) => {
        setIsOpen(false);
        setHighlightIndex(-1);
        if (restoreFocus) {
            buttonRef.current?.focus();
        }
    }, []);

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                closeDropdown(false);
            }
        },
        [closeDropdown],
    );

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        setHighlightIndex(-1);
    }, [search]);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setHighlightIndex((prev) =>
                        prev < filtered.length - 1 ? prev + 1 : prev,
                    );
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setHighlightIndex((prev) => (prev > 0 ? prev - 1 : 0));
                    break;
                case "Enter":
                case " ":
                    if (highlightIndex >= 0 && filtered[highlightIndex]) {
                        e.preventDefault();
                        window.location.href =
                            filtered[highlightIndex].StoreLinkString;
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    closeDropdown(true);
                    break;
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [closeDropdown, filtered, highlightIndex, isOpen]);

    useEffect(() => {
        if (highlightIndex >= 0) {
            const link = document.querySelector(
                `.weekly-offers-selection-block__item-${highlightIndex} a`,
            );
            if (link instanceof HTMLElement) {
                link.focus();
            }
        }
    }, [highlightIndex]);

    const highlightMatch = (text: string, search: string): React.ReactNode => {
        const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
        if (!terms.length) return text;

        let nodes: React.ReactNode[] = [text];

        terms.forEach((term) => {
            const regex = new RegExp(`(${term})`, "i");

            nodes = nodes.flatMap<React.ReactNode>((node) => {
                if (typeof node !== "string") return [node];

                return node
                    .split(regex)
                    .map((part, i) =>
                        part.toLowerCase() === term ? (
                            <strong key={`${i}-${part}-${Math.random()}`}>
                                {part}
                            </strong>
                        ) : (
                            part
                        ),
                    );
            });
        });

        return <>{nodes}</>;
    };

    return (
        <div
            className="weekly-offers-selection-block__wrapper"
            ref={wrapperRef}
        >
            <button
                className="weekly-offers-selection-block__button"
                onClick={() => {
                    if (isOpen) {
                        closeDropdown(true);
                    } else {
                        setIsOpen(true);
                    }
                }}
                ref={buttonRef}
            >
                <span>{translations.i18nButtonText}</span>
                <img
                    alt=""
                    className={`weekly-offers-selection-block__button-icon ${isOpen ? "caret-up" : "caret-down"}`}
                    src="/static/images/Icon-caret-down-green.svg"
                    height="12"
                    width="12"
                />
            </button>

            {isOpen && (
                <div className="weekly-offers-selection-block__dropdown">
                    <div className="weekly-offers-selection-block__input-wrapper">
                        <input
                            type="text"
                            id="store-search"
                            className="weekly-offers-selection-block__input"
                            placeholder={translations.i18nInputPlaceholder}
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                            }}
                            ref={inputRef}
                            aria-controls="store-list"
                            aria-activedescendant={
                                highlightIndex >= 0
                                    ? `store-${highlightIndex}`
                                    : undefined
                            }
                        />
                    </div>

                    <ul
                        aria-labelledby="store-search"
                        id="store-list"
                        className="weekly-offers-selection-block__list list-group"
                    >
                        {filtered.length > 0 ? (
                            filtered.map((loc, idx) => (
                                <li
                                    key={idx}
                                    className={`weekly-offers-selection-block__item weekly-offers-selection-block__item-${idx}`}
                                >
                                    <a
                                        className="weekly-offers-selection-block__link"
                                        data-click-type="store"
                                        href={loc.StoreLinkString}
                                        id={`store-${idx}`}
                                    >
                                        <span className="weekly-offers-selection-block__link-name">
                                            {highlightMatch(loc.Name, search)}
                                        </span>

                                        <span className="weekly-offers-selection-block__link-address">
                                            <span>
                                                {highlightMatch(
                                                    loc.Street!,
                                                    search,
                                                )}
                                                ,{" "}
                                            </span>
                                            <span>
                                                {highlightMatch(
                                                    loc.Zip!,
                                                    search,
                                                )}{" "}
                                            </span>
                                            <span>
                                                {highlightMatch(
                                                    loc.City,
                                                    search,
                                                )}
                                            </span>
                                        </span>
                                    </a>
                                </li>
                            ))
                        ) : (
                            <li className="weekly-offers-selection-block__item weekly-offers-selection-block__item--no-result">
                                {translations.i18nNoResults}
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default WeeklyOffersSelection;
